import React, { useState, useEffect } from 'react';
import { API_URL } from '../config/api';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { toast } from 'sonner';
import { Building, Filter, Loader2, Image, MapPin, CalendarIcon, Square, Coins, Eye, Play, Plus, Pencil, Trash2, Home, Key } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import PostAnonAccountPrompt from '../components/PostAnonAccountPrompt';

const typeContratLabels = {
  location: 'Location',
  vente: 'Vente',
  location_vente: 'Location-Vente'
};

const typeContratColors = {
  location: 'bg-blue-100 text-blue-800',
  vente: 'bg-green-100 text-green-800',
  location_vente: 'bg-purple-100 text-purple-800'
};

const LogementCard = ({ logement, sites, onRequestVisite, onEdit, onDelete, isAgentOrAdmin }) => {
  const site = sites.find(s => s.site_id === logement.site_id);
  
  // Determine status tag
  const isLoue = logement.loue || logement.statut === 'loue';
  const isVendu = logement.vendu || logement.statut === 'vendu';
  const isAttribue = logement.attribue;
  
  return (
    <Card className="card-hover bg-card border-none rounded-2xl overflow-hidden" data-testid={`logement-card-${logement.logement_id}`}>
      <div className="relative h-48">
        {logement.images?.[0] ? (
          <img src={logement.images[0]} alt="Logement" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-muted flex items-center justify-center">
            <Image className="w-12 h-12 text-muted-foreground" />
          </div>
        )}
        <div className="absolute top-3 left-3 flex gap-2">
          <Badge className={typeContratColors[logement.type_contrat]}>
            {typeContratLabels[logement.type_contrat]}
          </Badge>
        </div>
        <div className="absolute top-3 right-3 flex flex-col gap-1">
          {isLoue && (
            <Badge className="bg-orange-500 text-white">
              <Key className="w-3 h-3 mr-1" /> Loué
            </Badge>
          )}
          {isVendu && (
            <Badge className="bg-red-600 text-white">
              <Home className="w-3 h-3 mr-1" /> Vendu
            </Badge>
          )}
          {isAttribue && !isLoue && !isVendu && (
            <Badge className="bg-yellow-500 text-white">Attribué</Badge>
          )}
        </div>
        {logement.videos?.length > 0 && (
          <div className="absolute bottom-3 right-3">
            <Badge className="bg-black/70 text-white">
              <Play className="w-3 h-3 mr-1" /> {logement.videos.length} vidéo(s)
            </Badge>
          </div>
        )}
      </div>
      <CardContent className="p-4 space-y-3">
        <h3 className="text-xl font-semibold font-serif">{logement.titre || `Logement ${logement.type_contrat}`}</h3>
        
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-primary" />
            <span>{site?.ville || 'Non spécifié'}</span>
          </div>
          <div className="flex items-center gap-2">
            <Square className="w-4 h-4 text-primary" />
            <span>{logement.surface} m²</span>
          </div>
          <div className="flex items-center gap-2 col-span-2">
            <Coins className="w-4 h-4 text-primary" />
            <span className="font-semibold text-primary">{logement.prix?.toLocaleString()} FCFA{logement.type_contrat === 'location' ? '/mois' : ''}</span>
          </div>
        </div>
        
        {logement.description && (
          <p className="text-sm text-muted-foreground line-clamp-2">{logement.description}</p>
        )}
      </CardContent>
      <CardFooter className="p-4 pt-0 flex flex-col gap-2">
        <div className="flex gap-2 w-full">
          {site && (
            <Link to={`/sites/${site.site_id}`} className="flex-1">
              <Button variant="outline" className="w-full">
                <Eye className="w-4 h-4 mr-2" /> Voir site
              </Button>
            </Link>
          )}
          <Button 
            className="flex-1 btn-pill"
            disabled={isLoue || isVendu || isAttribue}
            onClick={() => onRequestVisite(logement)}
          >
            <CalendarIcon className="w-4 h-4 mr-2" />
            {isLoue || isVendu ? 'Indisponible' : 'Visiter'}
          </Button>
        </div>
        {isAgentOrAdmin && (
          <div className="flex gap-2 w-full">
            <Button size="sm" variant="outline" className="flex-1" onClick={() => onEdit(logement)}>
              <Pencil className="w-3 h-3 mr-1" /> Modifier
            </Button>
            <Button size="sm" variant="outline" className="text-red-600" onClick={() => onDelete(logement)}>
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        )}
      </CardFooter>
    </Card>
  );
};

export default function Logements() {
  const navigate = useNavigate();
  const { user, token, isAgentOrAdmin } = useAuth();
  const [logements, setLogements] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedLogement, setSelectedLogement] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [requesting, setRequesting] = useState(false);
  // Anon flow
  const [anonData, setAnonData] = useState({ nom: '', prenom: '', email: '', telephone: '' });
  const [postPromptOpen, setPostPromptOpen] = useState(false);
  const [postPromptUserExists, setPostPromptUserExists] = useState(false);
  
  // Edit/Create
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingLogement, setEditingLogement] = useState(null);
  const [formData, setFormData] = useState({
    titre: '', description: '', type_contrat: 'vente', prix: '', surface: '',
    site_id: '', images: [], videos: [], loue: false, vendu: false
  });

  const [filters, setFilters] = useState({
    type_contrat: '', site_id: '', prix_min: '', prix_max: '', surface_min: ''
  });

  const fetchData = async () => {
    try {
      const [logRes, sitesRes] = await Promise.all([
        fetch(`${API_URL}/api/logements`),
        fetch(`${API_URL}/api/sites`)
      ]);
      setLogements(await logRes.json());
      setSites(await sitesRes.json());
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleCreate = () => {
    setEditingLogement(null);
    setFormData({
      titre: '', description: '', type_contrat: 'vente', prix: '', surface: '',
      site_id: '', images: [], videos: [], loue: false, vendu: false
    });
    setEditDialogOpen(true);
  };

  const handleEdit = (logement) => {
    setEditingLogement(logement);
    setFormData({
      titre: logement.titre || '',
      description: logement.description || '',
      type_contrat: logement.type_contrat || 'vente',
      prix: logement.prix?.toString() || '',
      surface: logement.surface?.toString() || '',
      site_id: logement.site_id || '',
      images: logement.images || [],
      videos: logement.videos || [],
      loue: logement.loue || false,
      vendu: logement.vendu || false
    });
    setEditDialogOpen(true);
  };

  const handleDelete = async (logement) => {
    if (!window.confirm('Supprimer ce logement ?')) return;
    try {
      const response = await fetch(`${API_URL}/api/logements/${logement.logement_id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Erreur');
      toast.success('Logement supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };
      const body = {
        ...formData,
        prix: parseFloat(formData.prix) || 0,
        surface: parseFloat(formData.surface) || 0,
        images: formData.images.filter(i => i.trim()),
        videos: formData.videos.filter(v => v.trim())
      };
      
      const url = editingLogement ? `${API_URL}/api/logements/${editingLogement.logement_id}` : `${API_URL}/api/logements`;
      const response = await fetch(url, {
        method: editingLogement ? 'PUT' : 'POST',
        headers, body: JSON.stringify(body)
      });

      if (!response.ok) throw new Error('Erreur');
      toast.success(editingLogement ? 'Logement mis à jour' : 'Logement créé');
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleRequestVisite = (logement) => {
    setSelectedLogement(logement);
    setDialogOpen(true);
  };

  const handleConfirmVisite = async () => {
    if (!selectedDate) {
      toast.error('Veuillez sélectionner une date');
      return;
    }
    if (!user && (!anonData.nom || !anonData.prenom || !anonData.email || !anonData.telephone)) {
      toast.error('Renseignez vos coordonnées complètes');
      return;
    }
    setRequesting(true);
    try {
      let response;
      if (user && token) {
        response = await fetch(`${API_URL}/api/visites`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          credentials: 'include',
          body: JSON.stringify({
            type_visite: 'LOGEMENT',
            logement_id: selectedLogement.logement_id,
            date_visite: selectedDate.toISOString()
          })
        });
      } else {
        response = await fetch(`${API_URL}/api/visites/anonyme`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type_visite: 'LOGEMENT',
            logement_id: selectedLogement.logement_id,
            date_visite: selectedDate.toISOString(),
            ...anonData
          })
        });
      }

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Erreur');
      }
      const data = await response.json();
      setDialogOpen(false);
      if (user) {
        toast.success('Demande de visite envoyée');
      } else {
        setPostPromptUserExists(!!data.user_exists);
        setPostPromptOpen(true);
      }
    } catch (error) {
      toast.error(error.message || 'Erreur lors de la demande');
    } finally {
      setRequesting(false);
    }
  };

  const filteredLogements = logements.filter(l => {
    if (filters.type_contrat && l.type_contrat !== filters.type_contrat) return false;
    if (filters.site_id && l.site_id !== filters.site_id) return false;
    if (filters.prix_min && l.prix < parseInt(filters.prix_min)) return false;
    if (filters.prix_max && l.prix > parseInt(filters.prix_max)) return false;
    if (filters.surface_min && l.surface < parseInt(filters.surface_min)) return false;
    return true;
  });

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Building className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-4xl font-bold font-serif">Logements</h1>
              <p className="text-muted-foreground">Trouvez le logement idéal</p>
            </div>
          </div>
          {isAgentOrAdmin() && (
            <Button onClick={handleCreate} className="btn-pill">
              <Plus className="w-4 h-4 mr-2" /> Ajouter un logement
            </Button>
          )}
        </div>

        {/* Filters */}
        <Card className="mb-8 p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[150px]">
              <Label className="text-xs mb-1 block">Type</Label>
              <Select value={filters.type_contrat} onValueChange={(v) => setFilters({...filters, type_contrat: v === 'all' ? '' : v})}>
                <SelectTrigger><Filter className="w-4 h-4 mr-2" /><SelectValue placeholder="Tous" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  {Object.entries(typeContratLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-[150px]">
              <Label className="text-xs mb-1 block">Site</Label>
              <Select value={filters.site_id} onValueChange={(v) => setFilters({...filters, site_id: v === 'all' ? '' : v})}>
                <SelectTrigger><MapPin className="w-4 h-4 mr-2" /><SelectValue placeholder="Tous" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les sites</SelectItem>
                  {sites.map(s => <SelectItem key={s.site_id} value={s.site_id}>{s.nom || s.ville}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredLogements.map(logement => (
            <LogementCard
              key={logement.logement_id}
              logement={logement}
              sites={sites}
              onRequestVisite={handleRequestVisite}
              onEdit={handleEdit}
              onDelete={handleDelete}
              isAgentOrAdmin={isAgentOrAdmin()}
            />
          ))}
        </div>

        {filteredLogements.length === 0 && (
          <div className="text-center py-12">
            <Building className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">Aucun logement trouvé</p>
          </div>
        )}

        {/* Visite Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Demander une visite</DialogTitle>
              <DialogDescription>Sélectionnez une date pour visiter ce logement</DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                disabled={(date) => date < new Date()}
                locale={fr}
                className="rounded-md border mx-auto"
              />
              {!user && (
                <div className="space-y-3 border-t pt-4">
                  <p className="text-sm text-muted-foreground">Vos coordonnées (vous n'êtes pas connecté)</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">Prénom *</Label><Input value={anonData.prenom} onChange={(e) => setAnonData({...anonData, prenom: e.target.value})} data-testid="anon-prenom-input" /></div>
                    <div><Label className="text-xs">Nom *</Label><Input value={anonData.nom} onChange={(e) => setAnonData({...anonData, nom: e.target.value})} data-testid="anon-nom-input" /></div>
                  </div>
                  <div><Label className="text-xs">Email *</Label><Input type="email" value={anonData.email} onChange={(e) => setAnonData({...anonData, email: e.target.value})} data-testid="anon-email-input" /></div>
                  <div><Label className="text-xs">Téléphone *</Label><Input value={anonData.telephone} onChange={(e) => setAnonData({...anonData, telephone: e.target.value})} data-testid="anon-telephone-input" /></div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
              <Button
                onClick={handleConfirmVisite}
                disabled={requesting || !selectedDate || (!user && (!anonData.nom || !anonData.prenom || !anonData.email || !anonData.telephone))}
                data-testid="confirm-visite-btn"
              >
                {requesting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Confirmer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <PostAnonAccountPrompt
          open={postPromptOpen}
          onOpenChange={setPostPromptOpen}
          userExists={postPromptUserExists}
          prefillNom={anonData.nom}
          prefillPrenom={anonData.prenom}
          prefillEmail={anonData.email}
          prefillTelephone={anonData.telephone}
        />

        {/* Edit/Create Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingLogement ? 'Modifier le logement' : 'Nouveau logement'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div><Label>Titre</Label><Input value={formData.titre} onChange={(e) => setFormData({...formData, titre: e.target.value})} /></div>
              <div><Label>Description</Label><Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} rows={3} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Type de contrat *</Label>
                  <Select value={formData.type_contrat} onValueChange={(v) => setFormData({...formData, type_contrat: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(typeContratLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Site</Label>
                  <Select value={formData.site_id} onValueChange={(v) => setFormData({...formData, site_id: v})}>
                    <SelectTrigger><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                    <SelectContent>{sites.map(s => <SelectItem key={s.site_id} value={s.site_id}>{s.nom || s.ville}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Prix (FCFA)</Label><Input type="number" value={formData.prix} onChange={(e) => setFormData({...formData, prix: e.target.value})} /></div>
                <div><Label>Surface (m²)</Label><Input type="number" value={formData.surface} onChange={(e) => setFormData({...formData, surface: e.target.value})} /></div>
              </div>
              <div>
                <Label>URLs des images (une par ligne)</Label>
                <Textarea value={formData.images.join('\n')} onChange={(e) => setFormData({...formData, images: e.target.value.split('\n')})} rows={2} />
              </div>
              <div>
                <Label>URLs des vidéos (une par ligne)</Label>
                <Textarea value={formData.videos.join('\n')} onChange={(e) => setFormData({...formData, videos: e.target.value.split('\n')})} rows={2} />
              </div>
              <div className="flex gap-6">
                <div className="flex items-center gap-2">
                  <Switch checked={formData.loue} onCheckedChange={(v) => setFormData({...formData, loue: v, vendu: v ? false : formData.vendu})} />
                  <Label>Loué</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={formData.vendu} onCheckedChange={(v) => setFormData({...formData, vendu: v, loue: v ? false : formData.loue})} />
                  <Label>Vendu</Label>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Annuler</Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {editingLogement ? 'Mettre à jour' : 'Créer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
